import styled, { css } from 'styled-components';
import type { ElementType } from 'react';
import Colours from '/shared/styled/Colours';

interface Props {
  $legal?: boolean;
  $alpha?: boolean;
  $roman?: boolean;
  $hasLargerDot?: boolean;
  $hasExtraLargeDot?: boolean;
  $hasExtraLiPadding?: boolean;
  $hasLargeLiBottomMargin?: boolean;
  $hasExtraLargeLiBottomMargin?: boolean;
  $grey?: boolean;
  $white?: boolean;
  $darkGrey?: boolean;
  $smallText?: boolean;
  $hasExtraMarginBottom?: boolean;
  as?: ElementType;
}

function ulStyle({ as = 'ul' }: Props) {
  if (as === 'ul') {
    return css`
      li {
        &::before {
          content: '•';
        }
      }
    `;
  }
}

function legalStyle() {
  return css`
    padding-left: 1.5em;
    padding-bottom: 1.5rem;

    > li {
      margin-bottom: 1rem;
      counter-increment: custom;

      &::before {
        content: counter(custom) ' ';
        margin-left: -1.5em;
      }

      > ol {
        counter-reset: custom;
        padding-left: 5em !important;
      }

      > ol > li {
        counter-increment: custom;

        &::before {
          content: counters(custom, '.') '. ';
          margin-left: -3em;
        }

        > ol > li {
          counter-increment: layer2;

          &::before {
            content: counter(layer2, lower-alpha) ') ';
            margin-left: -2em;
          }

          > ol > li {
            counter-increment: layer3;

            &::before {
              content: counter(layer3, lower-roman) ') ';
              margin-left: -2em;
            }
          }
        }
      }
    }
  `;
}

function alphaStyle({ $alpha = false }: Props) {
  if ($alpha) {
    return css`
      content: counter(custom, lower-alpha) ' ';
    `;
  }
}

function romanStyle({ $roman = false }: Props) {
  if ($roman) {
    return css`
      content: counter(custom, lower-roman) ' ';
    `;
  }
}

function olStyle({ as = 'ul', $legal }: Props) {
  if (as === 'ol') {
    if ($legal) {
      return legalStyle();
    }
    return css`
      padding-left: 1em;

      li {
        counter-increment: custom;

        &::before {
          content: counter(custom) ' ';
          margin-left: -1em;
          ${alphaStyle}
          ${romanStyle}
        }

        &:first-child {
          counter-reset: custom;
        }
      }
    `;
  }
}

function hasLargerDotStyle({ $hasLargerDot = false }: Props) {
  if ($hasLargerDot) {
    return css`
      li {
        &::before {
          font-size: 17px;
          font-weight: 800;
          margin-left: -0.65em;
        }
      }
    `;
  }
}
function hasExtraLargeDotStyle({ $hasExtraLargeDot = false }: Props) {
  if ($hasExtraLargeDot) {
    return css`
      li {
        padding-left: 15px;

        &::before {
          font-size: 50px;
          line-height: 30px;
          font-weight: 800;
          margin-left: -25px;
        }
      }
    `;
  }
}

function hasExtraLiPaddingStyle({ $hasExtraLiPadding = false }: Props) {
  if ($hasExtraLiPadding) {
    return css`
      li {
        padding-top: 0.6rem;
        padding-bottom: 0.6rem;
      }
    `;
  }
}

function hasLargeLiBottomMarginStyle({ $hasLargeLiBottomMargin = false }: Props) {
  if ($hasLargeLiBottomMargin) {
    return css`
      li {
        margin-bottom: 0.8rem;
      }
    `;
  }
}

function hasExtraLargeLiBottomMarginStyle({ $hasExtraLargeLiBottomMargin = false }: Props) {
  if ($hasExtraLargeLiBottomMargin) {
    return css`
      li {
        margin-bottom: 2rem;
      }
    `;
  }
}

function greyStyle({ $grey = false }: Props) {
  if ($grey) {
    return css`
      li {
        &::before {
          color: ${Colours.grey};
        }
      }
    `;
  }
}
function whiteStyle({ $white = false }: Props) {
  if ($white) {
    return css`
      li {
        &::before {
          color: ${Colours.white};
        }
      }
    `;
  }
}

function darkGreyStyle({ $darkGrey = false }: Props) {
  if ($darkGrey) {
    return css`
      li {
        &::before {
          color: ${Colours.black};
        }
      }
    `;
  }
}

function smallTextStyle({ $smallText = false }: Props) {
  if ($smallText) {
    return css`
      li {
        font-size: 13px;
      }
    `;
  }
}

function hasExtraMarginBottomStyle({ $hasExtraMarginBottom = false }: Props) {
  if ($hasExtraMarginBottom) {
    return css`
      margin-bottom: 2rem !important;
    `;
  }
}

export default styled.ul<Props>`
  list-style: none;
  padding-left: 0.75em;

  &,
  li {
    list-style: none;
  }

  li {
    line-height: 1.6;
    font-weight: 300;
    margin-bottom: 0.25rem;

    &::before {
      content: '•';
      color: ${Colours.lime};
      position: absolute;
      margin-left: -0.75em;
    }

    > ul,
    ol {
      padding-left: 2rem;
      padding-top: 0.75rem;
    }
  }
  ${ulStyle}
  ${olStyle}
  ${hasExtraLiPaddingStyle}
  ${greyStyle}
  ${whiteStyle}
  ${darkGreyStyle}
  ${hasLargeLiBottomMarginStyle}
  ${hasExtraLargeLiBottomMarginStyle}
  ${hasLargerDotStyle}
  ${smallTextStyle}
  ${hasExtraLargeDotStyle}
  ${hasExtraMarginBottomStyle}
`;
